import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiFetch } from '../../api/client';

function parseDiasVencimento(dataFim) {
  if (!dataFim) return null;
  let vencimento;
  if (String(dataFim).includes('/') && String(dataFim).split('/').length === 3) {
    const [day, month, year] = dataFim.split('/');
    vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  } else {
    vencimento = new Date(dataFim);
  }
  if (isNaN(vencimento.getTime())) return null;
  const hoje = new Date();
  return Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
}

function formatCurrency(valor) {
  const num = parseFloat(valor);
  if (!valor || isNaN(num)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
}

function getComplianceColor(sla) {
  if (sla >= 90) return 'success';
  if (sla >= 70) return 'warning';
  return 'error';
}

function isAvaliacaoEmDia(prestador, avaliacoes) {
  const hoje = new Date();
  const limite90 = 90 * 24 * 60 * 60 * 1000;
  const avs = avaliacoes.filter((a) => a.numeroContrato === prestador.numeroContrato);
  if (!avs.length) return false;
  return avs.some((av) => {
    const d = new Date(av.dataAvaliacao);
    return !isNaN(d.getTime()) && hoje - d <= limite90;
  });
}

/**
 * Dashboard estratégico de indicadores — Setor, Empresas e Prazos.
 * @param {{ data: Array, avaliacoes: Array }} props
 */
export default function DashboardInteligente({ data = [], avaliacoes = [] }) {
  const [aba, setAba] = useState(0);
  const [cobrandoId, setCobrandoId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCobrarFiscal = async (c) => {
    if (!c?.numeroContrato || cobrandoId) return;
    setCobrandoId(c.numeroContrato);
    try {
      const res = await apiFetch('/api/avaliacao/cobrar-fiscal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numeroContrato: c.numeroContrato,
          empresa: c.empresa,
          setor: c.setor,
          vigenciaFim: c.vigenciaFim,
          dias: c.dias
        })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || 'Falha ao enviar e-mail ao fiscal.');
      setSnackbar({
        open: true,
        message: payload.message || 'E-mail de cobrança enviado ao fiscal.',
        severity: 'success'
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Erro ao cobrar fiscal.',
        severity: 'error'
      });
    } finally {
      setCobrandoId(null);
    }
  };

  const compliancePorSetor = useMemo(() => {
    const mapa = {};
    data.forEach((p) => {
      const setor = p.setor || 'Não informado';
      if (!mapa[setor]) mapa[setor] = { setor, total: 0, emDia: 0 };
      mapa[setor].total += 1;
      if (isAvaliacaoEmDia(p, avaliacoes)) mapa[setor].emDia += 1;
    });
    return Object.values(mapa)
      .map((s) => ({
        ...s,
        sla: s.total > 0 ? Math.round((s.emDia / s.total) * 100) : 0
      }))
      .sort((a, b) => b.sla - a.sla);
  }, [data, avaliacoes]);

  const matrizRisco = useMemo(() => {
    const porEmpresa = {};
    data.forEach((p) => {
      const nome = p.empresa || 'Sem nome';
      if (!porEmpresa[nome]) {
        porEmpresa[nome] = {
          empresa: nome,
          setor: p.setor,
          valorMensal: 0,
          notas: []
        };
      }
      porEmpresa[nome].valorMensal += parseFloat(p.valorMes) || 0;
      if (p.setor && !porEmpresa[nome].setor) porEmpresa[nome].setor = p.setor;
    });

    avaliacoes.forEach((av) => {
      if (porEmpresa[av.empresa]) {
        porEmpresa[av.empresa].notas.push(Number(av.nota) || 0);
      }
    });

    const empresas = Object.values(porEmpresa).map((e) => ({
      ...e,
      notaMedia: e.notas.length
        ? Math.round((e.notas.reduce((a, b) => a + b, 0) / e.notas.length) * 10) / 10
        : null
    }));

    const valores = empresas.map((e) => e.valorMensal).filter((v) => v > 0);
    const mediaValor = valores.length
      ? valores.reduce((a, b) => a + b, 0) / valores.length
      : 0;

    const criticas = empresas
      .filter((e) => e.notaMedia !== null && e.notaMedia < 3 && e.valorMensal > mediaValor)
      .sort((a, b) => b.valorMensal - a.valorMensal)
      .slice(0, 5);

    const melhores = empresas
      .filter((e) => e.notaMedia !== null)
      .sort((a, b) => b.notaMedia - a.notaMedia)
      .slice(0, 5);

    return { criticas, melhores, mediaValor };
  }, [data, avaliacoes]);

  const radarVencimentos = useMemo(() => {
    const critico = [];
    const atencao = [];
    const confortavel = [];

    data.forEach((p) => {
      const dias = parseDiasVencimento(p.vigenciaFim);
      const item = {
        numeroContrato: p.numeroContrato,
        empresa: p.empresa,
        setor: p.setor,
        dias,
        vigenciaFim: p.vigenciaFim
      };
      if (dias === null) {
        atencao.push(item);
      } else if (dias <= 30) {
        critico.push(item);
      } else if (dias <= 90) {
        atencao.push(item);
      } else {
        confortavel.push(item);
      }
    });

    critico.sort((a, b) => (a.dias ?? 0) - (b.dias ?? 0));

    return {
      contadores: {
        critico: critico.length,
        atencao: atencao.length,
        confortavel: confortavel.length
      },
      criticos: critico
    };
  }, [data]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Tabs
        value={aba}
        onChange={(_, v) => setAba(v)}
        variant="fullWidth"
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem' }
        }}
      >
        <Tab label="Compliance por Setor" />
        <Tab label="Matriz de Risco" />
        <Tab label="Radar de Vencimentos" />
      </Tabs>

      {aba === 0 && (
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
            Performance do Setor — SLA de Avaliação
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Percentual de contratos com avaliação registrada nos últimos 90 dias.
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Setor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Contratos</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Em dia</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>SLA</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {compliancePorSetor.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum dado disponível.
                    </TableCell>
                  </TableRow>
                ) : (
                  compliancePorSetor.map((row, idx) => (
                    <TableRow key={row.setor} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.setor}</Typography>
                      </TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.emDia}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={row.sla}
                            color={getComplianceColor(row.sla)}
                            sx={{ flex: 1, height: 10, borderRadius: 2 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.sla}%`}
                          size="small"
                          color={getComplianceColor(row.sla)}
                          sx={{ fontWeight: 700, borderRadius: 2 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {aba === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 2,
                bgcolor: 'error.light',
                color: 'error.contrastText',
                height: '100%'
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <WarningIcon />
                  <Typography variant="h6" fontWeight={700}>
                    Top 5 — Maior Risco
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 2 }}>
                  Valor acima da média ({formatCurrency(matrizRisco.mediaValor)}) e nota &lt; 3,0
                </Typography>
                {matrizRisco.criticas.length === 0 ? (
                  <Typography variant="body2">Nenhum fornecedor crítico identificado.</Typography>
                ) : (
                  <List dense disablePadding>
                    {matrizRisco.criticas.map((e, i) => (
                      <ListItem key={e.empresa} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                          <Typography fontWeight={800}>{i + 1}.</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={e.empresa}
                          secondary={`${e.setor || '-'} • ${formatCurrency(e.valorMensal)} • Nota ${e.notaMedia}`}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 2,
                background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)',
                color: 'white',
                height: '100%'
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TrendingUpIcon />
                  <Typography variant="h6" fontWeight={700}>
                    Top 5 — Melhores Empresas
                  </Typography>
                </Box>
                {matrizRisco.melhores.length === 0 ? (
                  <Typography variant="body2">Sem avaliações suficientes.</Typography>
                ) : (
                  <List dense disablePadding>
                    {matrizRisco.melhores.map((e, i) => (
                      <ListItem key={e.empresa} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                          <CheckCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${i + 1}. ${e.empresa}`}
                          secondary={`${e.setor || '-'} • Nota ${e.notaMedia} • ${formatCurrency(e.valorMensal)}/mês`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {aba === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
            Radar de Vencimentos
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                key: 'critico',
                label: 'Crítico (< 30 dias ou Vencido)',
                valor: radarVencimentos.contadores.critico,
                gradient: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)'
              },
              {
                key: 'atencao',
                label: 'Atenção (30 a 90 dias)',
                valor: radarVencimentos.contadores.atencao,
                gradient: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)'
              },
              {
                key: 'confortavel',
                label: 'Confortável (> 90 dias)',
                valor: radarVencimentos.contadores.confortavel,
                gradient: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)'
              }
            ].map((c) => (
              <Grid item xs={12} md={4} key={c.key}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    background: c.gradient,
                    color: 'white',
                    textAlign: 'center'
                  }}
                >
                  <CardContent sx={{ py: 3 }}>
                    <ScheduleIcon sx={{ fontSize: 36, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h3" fontWeight={800}>
                      {c.valor}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, mt: 1 }}>
                      {c.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <NotificationsActiveIcon color="error" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Contratos Críticos — Ação sobre o Fiscal
                </Typography>
              </Box>
              {radarVencimentos.criticos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum contrato em situação crítica no momento.
                </Typography>
              ) : (
                <List dense>
                  {radarVencimentos.criticos.map((c) => (
                    <ListItem
                      key={c.numeroContrato}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        bgcolor: 'error.light',
                        color: 'error.contrastText'
                      }}
                      secondaryAction={
                        <Tooltip title="Cobrar fiscal por e-mail">
                          <span>
                            <IconButton
                              edge="end"
                              sx={{ color: 'inherit' }}
                              disabled={cobrandoId === c.numeroContrato}
                              onClick={() => handleCobrarFiscal(c)}
                              aria-label={`Cobrar fiscal do contrato ${c.numeroContrato}`}
                            >
                              {cobrandoId === c.numeroContrato ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <NotificationsActiveIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                        <BusinessIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${c.numeroContrato} — ${c.empresa}`}
                        secondary={`${c.setor || '-'} • Vence em ${c.dias ?? '?'} dias (${c.vigenciaFim || '-'})`}
                        primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.9 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
